import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getTrips, getTripById } from "../api/trips";
import { getCityDisplay } from "../utils/city";
import dayjs from "dayjs";

const TripSelect = ({ value, onChange, dateFrom }) => {
  const { t } = useTranslation("common");
  const [trips, setTrips] = useState(value?.id ? [value] : []);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  const fetchTrips = async (pageNumber = 1, searchText = "") => {
    setLoading(true);
    try {
      const params = {
        page: pageNumber,
        search: searchText,
      }
      if (!searchText) {
        params['date_from'] = dateFrom || new Date();
      }
      const response = await getTrips(params);
      const newTrips = pageNumber === 1 ? response.results : [...trips, ...response.results];
      setTrips(newTrips);
      setHasMore(Boolean(response.next));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (value && value.id && !value.route && !value.label) {
      getTripById(value.id).then((trip) => {
        const from = getCityDisplay(trip.route?.stops?.[0]?.city) || "";
        const to = getCityDisplay(trip.route?.stops?.slice(-1)?.[0]?.city) || "";
        const label = `${trip.route?.name || `${from} → ${to}`} (${dayjs(trip.departure_date).format("DD.MM.YYYY")})`;
        const enriched = { ...trip, label };
        setTrips((prev) => {
          const without = prev.filter((t) => String(t.id) !== String(trip.id));
          return [enriched, ...without];
        });
        onChange(enriched);
      });
    } else if (!value?.id) {
      fetchTrips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      if (!search.includes('→')) {
        fetchTrips(1, search);
      }
    }, 400);
    return () => clearTimeout(searchTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
      !loading &&
      hasMore
    ) {
      const nextPage = page + 1;
      setPage(nextPage);

      if (!search.includes('→')) {
        fetchTrips(nextPage, search);
      } else {
        fetchTrips(nextPage, "");
      }
    }
  };

  return (
    <Autocomplete
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      inputValue={search}
      onInputChange={(event, newInputValue) => setSearch(newInputValue)}
      options={trips}
      sx={{width: "100%"}}
      getOptionLabel={(trip) => {
        if (!trip) return "";
        if (trip.label) return trip.label;
        const routeName =
          (typeof trip.route === "string" ? trip.route : trip.route?.name) || "";
        const stops = trip?.route?.stops;
        const from = getCityDisplay(stops?.[0]?.city) || "";
        const to = getCityDisplay(stops?.[stops?.length - 1]?.city) || "";
        const title = routeName || (from && to ? `${from} → ${to}` : "") || `#${trip.id}`;
        const date = trip.departure_date
          ? dayjs(trip.departure_date).format("YYYY-MM-DD")
          : "";
        const freeSeats = trip.sold_out
          ? t("tripSelect.soldOut")
          : typeof trip.free_seats === "number"
            ? trip.free_seats > 0
              ? t("tripSelect.freeSeatsLeft", { count: trip.free_seats })
              : t("tripSelect.noSeatsLeft")
            : "";
        return [title, date ? `(${date})` : "", freeSeats].filter(Boolean).join(" ");
      }}
      isOptionEqualToValue={(option, val) =>
        String(option?.id) === String(val?.id)
      }
      loading={loading}
      fullWidth
      ListboxProps={{
        onScroll: handleScroll,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("tripSelect.selectTrip")}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default TripSelect;